// Sending scans to the server.
//
// Always after they are on the phone, never instead of it. A scan taken with no
// signal has to survive, which it only does if saving comes first.
//
// Three outcomes, and keeping them apart is what stops scans being lost. A scan the
// server judged is finished with, whatever the judgement. A scan that could not reach
// the server stays waiting, and no retry is spent on it.
//
// Two kinds of thing wait here: lead scans, sent in batches, and door decisions, sent
// one at a time through the door composable that owns those routes.

export type UploadOutcome = 'captured' | 'already_captured' | 'rejected' | 'unreachable'

interface ScanResponse {
  status: string
}

interface BatchResponse {
  // Keyed by the identifier the phone gave each scan, which is how a reply is matched
  // back to the record it belongs to.
  results: Record<string, { status: string }>
}

// Matches the server's limit. A day's backlog goes up as a handful of requests rather
// than several hundred, and a failed one costs little to repeat.
const BATCH_SIZE = 100

// Both requests are bounded. Nothing is lost by giving up on either: the scan is in the
// phone's own store before any of this runs, and an abandoned request settles as
// unreachable and waits to be sent again, which is what noteAttempt is for. Without a
// ceiling that path is never reached and the phone simply stops, mid upload, with
// nothing on screen saying why.
//
// Neither asks ofetch for a retry, and ofetch does not retry a POST by default. Retrying
// here is somebody pressing Upload now again.
//
// One scan, sent as it is taken, with somebody standing at the counter. Cheap to
// abandon: scan_uuid means a record that did reach us collapses onto the same row when
// it is sent again, so the cost of being wrong is one repeated request.
//
// Applied through boundedSignal rather than ofetch's `timeout`, which this request can
// no longer use: it is skipped entirely once a signal of our own is passed, and this one
// carries the caller's Cancel. See boundedSignal for why that is worth spelling out.
const SEND_TIMEOUT_MS = 5000

// A batch of up to a hundred, started by hand from the maintenance panel. Far longer on
// purpose. A day's backlog going up over venue WiFi legitimately takes a while, and a
// door-sized ceiling here would abandon work the server is in the middle of committing -
// safe, because of the same deduplication, but wasted and then repeated. This is long
// enough to tell a stuck connection from a slow one.
//
// Per batch, deliberately, and not a ceiling on the whole drain: a long backlog is
// several of these in a row and is meant to be. The loop stops at the first batch that
// could not reach us, so nothing runs on and on, and one overall ceiling would instead
// abandon a batch the server was committing.
const BATCH_TIMEOUT_MS = 30000

export const useScanUpload = () => {
  const config = useRuntimeConfig()
  const device = useScanDevice()
  const { refresh } = useScanQueue()
  // The door's routes and request shape, which belong with the door. See uploadDecision.
  const { uploadDecision } = useWorkshopDoor()

  /**
   * Sends one scan and says what became of it.
   *
   * `cancel` is somebody on shift giving up on the wait, not on the scan. Aborting
   * settles this as unreachable, which leaves the record waiting exactly as a dead
   * connection would, so the two need no telling apart here.
   */
  const send = async (scan: LeadScan, cancel?: AbortSignal): Promise<UploadOutcome> => {
    if (!device.value.token || !device.value.eventId) {
      return 'unreachable'
    }

    // A scan from a pairing this phone has left. Left untouched rather than rejected:
    // the record is not at fault, and it is somebody's work even though it can no
    // longer be sent.
    if (!mayUpload(scan, device.value.pairingId)) {
      return 'unreachable'
    }

    const bounded = boundedSignal(SEND_TIMEOUT_MS, cancel)

    try {
      const response = await $fetch<ScanResponse>(
        `${config.public.apiBase}/scan/v1/events/${device.value.eventId}/scan`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${device.value.token}` },
          signal: bounded.signal,
          body: {
            code: scan.code,
            // The area chosen when the scan was taken, not the one selected now. A
            // shift that switches area mid drain must not move earlier scans.
            event_area_id: scan.areaId,
            scan_uuid: scan.uuid,
          },
        },
      )

      if (response.status === 'unknown_ticket' || response.status === 'unknown_area') {
        await markScan(scan.uuid, 'rejected', response.status)

        return 'rejected'
      }

      await markScan(scan.uuid, 'synced')

      return response.status === 'already_captured' ? 'already_captured' : 'captured'
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      const message = status ? `http ${status}` : 'no connection'

      // Anything the server answered is final. Retrying cannot change a refused key or
      // a malformed request, and a phone retrying for ever would never drain.
      if (status && status >= 400 && status < 500) {
        await markScan(scan.uuid, 'rejected', message)

        return 'rejected'
      }

      // A failure with no answer is the connection, and costs the scan nothing.
      await noteAttempt(scan.uuid, message)

      return 'unreachable'
    } finally {
      bounded.release()

      await refresh()
    }
  }

  /**
   * Applies what the server said about each scan in a batch.
   *
   * A scan the server judged is finished with, whichever way it went. One it could not
   * decide on stays waiting, and costs the record nothing.
   */
  const applyResults = async (
    batch: LeadScan[],
    results: BatchResponse['results'],
  ): Promise<number> => {
    let settled = 0

    for (const scan of batch) {
      const status = results[scan.uuid]?.status

      if (!status || status === 'failed') {
        // Missing from the reply entirely, or our side could not handle it. Either way
        // the phone keeps it.
        await noteAttempt(scan.uuid, status ?? 'no result returned')

        continue
      }

      if (status === 'unknown_ticket' || status === 'unknown_area') {
        await markScan(scan.uuid, 'rejected', status)
      } else {
        await markScan(scan.uuid, 'synced')
      }

      settled++
    }

    return settled
  }

  /**
   * Sends everything still waiting, oldest first, a batch at a time.
   *
   * Started by hand, from Upload now in the maintenance panel. Nothing retries on its
   * own: a scan taken with no connection stays waiting until someone uploads it as part
   * of the end of day routine. That keeps the phone predictable, and keeps a backlog
   * from going up at a moment nobody chose.
   *
   * One request at a time on purpose. Firing every batch at once would put the whole
   * backlog on the wire the moment venue WiFi returns, from every phone at once.
   *
   * Stops at the first batch that could not reach us, since the next would fail the
   * same way and spend an attempt on every record in it.
   */
  const drain = async (): Promise<{ sent: number, remaining: number }> => {
    const pairing = device.value.pairingId

    const waiting = (await scansByStatus(pairing, 'pending'))
      .sort((a, b) => a.scannedAt.localeCompare(b.scannedAt))

    if (!waiting.length || !device.value.token || !device.value.eventId) {
      return { sent: 0, remaining: waiting.length }
    }

    // The query already asked for this pairing alone. Checked again because this is the
    // line that turns a badge into a named partner's lead.
    const current = waiting.filter(scan => mayUpload(scan, pairing))
    const leads = current.filter(isLeadScan)
    const decisions = current.filter(isDoorScan)

    let sent = 0
    // Records that will never go up. Kept out of sent so the panel does not claim to
    // have sent them, and out of the remainder so it does not report a stuck backlog.
    let settled = 0

    for (let i = 0; i < leads.length; i += BATCH_SIZE) {
      const batch = leads.slice(i, i + BATCH_SIZE)

      try {
        const response = await $fetch<BatchResponse>(
          `${config.public.apiBase}/scan/v1/events/${device.value.eventId}/scan/batch`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${device.value.token}` },
            timeout: BATCH_TIMEOUT_MS,
            body: {
              scans: batch.map(scan => ({
                code: scan.code,
                event_area_id: scan.areaId,
                scan_uuid: scan.uuid,
              })),
            },
          },
        )

        sent += await applyResults(batch, response.results ?? {})
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status

        // The server answered and refused the whole request, most often because it was
        // malformed. Retrying unchanged cannot help, so the records are settled rather
        // than left to be sent for ever.
        if (status && status >= 400 && status < 500 && status !== 429) {
          for (const scan of batch) {
            await markScan(scan.uuid, 'rejected', `http ${status}`)
          }

          sent += batch.length

          continue
        }

        for (const scan of batch) {
          await noteAttempt(scan.uuid, status ? `http ${status}` : 'no connection')
        }

        break
      }
    }

    // One at a time, because each goes to the door it was made at rather than to one
    // endpoint taking a list. Only a decision the connection swallowed is ever here.
    //
    // Stops at the first unreachable one, for the same reason the batches above do.
    for (const decision of decisions) {
      const outcome = await uploadDecision(decision)

      if (outcome === 'unreachable') {
        break
      }

      if (outcome === 'sent') {
        sent++
      } else {
        settled++
      }
    }

    await refresh()

    // Counted from what was actually attempted rather than from everything read back,
    // so a record fenced above is not reported as a backlog somebody can clear.
    return { sent, remaining: current.length - sent - settled }
  }

  return { send, drain }
}
